<template>
  <div :class="_fs_class">
    <p v-if="hint">{{ hint }}</p>
    <p v-if="extensions" :id="id + '-file-type-hint'" class="cat-hint-text">
      {{
        resolveTxt(
          "skin.document.fileType.allowedHint",
          "Accepted file formats: "
        )
      }}{{ extension.join(", ").toUpperCase() }}
    </p>
    <p v-if="maxSize" :id="id + '-file-size-hint'" class="cat-hint-text">
      {{
        resolveTxt("skin.document.fileSize.maximumHint", "Maximum file size: ")
      }}{{ formatBytes(maxSize) }}
    </p>
    <slot></slot>
    <div
      v-if="isDragAndDropCapable"
      class="dragdrop"
      role="presentation"
      @dragover="$event.preventDefault()"
      @drop="_onChange"
      @click="clickUpload"
    >
      <em
        role="presentation"
        class="icon-cloud-outline symbol-upload nm mrs"
      ></em>
      <p class="nm np">
        {{
          resolveTxt(
            "skin.upload.link.before",
            "Drag your file(s) in here or choose a file to upload."
          )
        }}
        <cat-btn
          :id="id + '-upload-link'"
          :buttonTag="true"
          role="button"
          class="mln link small pn"
          :data-button="resolveTxt('skin.upload.link', 'Choose a file')"
          :aria-describedby="
            [
              extensions && id + '-file-type-hint',
              maxSize && id + '-file-size-hint'
            ]
              .filter(Boolean)
              .join(' ')
          "
          @click="clickUpload"
          >{{ resolveTxt("skin.upload.link", "Choose a file") }}</cat-btn
        >
        {{ resolveTxt("skin.upload.link.after", "") }}
      </p>
    </div>
    <input
      :id="id + '_upload'"
      ref="fileUpload"
      type="file"
      name="fileUpload"
      style="display: none"
      :multiple="maxFilesAllowed > 1"
      @change="_onChange"
    />
    <slot name="after-input"></slot>
    <cat-errors v-if="errs && errs.length" :errors="errs" />
  </div>
</template>

<script type="ts">
import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import CatErrors from "@/components/catErrors/catErrors.vue";
import CatBtn from "@/components/catBtn/catBtn.vue";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);
    const displayParams = inject("displayParams", false);

    return {
      resolveTxt,
      displayParams
    };
  },
  name: "CatSimpleUpload",
  components: {
    CatErrors,
    CatBtn
  },
  props: {
    id: {
      type: [Boolean, Number, String],
      description: "Document upload ID"
    },
    errors: {
      type: Array,
      description: "List of errors"
    },
    rejectedUploads: {
      type: Number,
      description: "Count of uploads that have failed"
    },
    readonly: {
      type: Boolean,
      description: "Shows document upload in read-only mode"
    },
    mandatory: {
      type: Boolean,
      default: false,
      description: "Indicates if the field is mandatory"
    },
    alias: {
      type: String,
      default: "fileUpload",
      description: "Name for document upload"
    },
    hint: {
      type: String,
      description: "Hint text for document upload"
    },
    extensions: {
      type: [String, Array],
      default: function () {
        return ["pdf", "jpg", "png"];
      },
      description: "Indicating upload format or file extension"
    },
    mimeTypes: {
      type: [String, Array],
      default: function () {
        return ["application/pdf", "image/jpeg", "image/png"];
      },
      description: "Indicates content type of a file and its format"
    },
    documentUploadDate: {
      type: [String],
      default: null,
      description: "Represents the date and time when a document was uploaded"
    },
    maxSize: {
      type: [String, Number],
      default: "1048576",
      description: "Maximum allowable size for a file "
    },
    fileTypeError: {
      type: [String],
      description: "Error related to the type or format of a file"
    },
    fileSizeError: {
      type: [String],
      description: "Error related to the size of a file"
    },
    fileSizeZeroError: {
      type: [String],
      description: "Error related to when a file has a size of zero bytes"
    },
    maxFilesAllowed: {
      type: Number,
      default: 1,
      description: "Maximum number of files allowed"
    },
    tooManyFilesError: {
      type: [String],
      description:
        "Error occurs when more files are tried than the maximum allowable limit"
    },
    lastRefreshId: {
      type: [String],
      default: null,
      description: "Most recent refresh or update of content"
    }
  },
  data: function () {
    return {
      file: null,
      errs: this.errors ? this.errors : [],
      extension:
        typeof this.extensions == "string"
          ? this.extensions.split(",")
          : this.extensions,
      documentTypes:
        typeof this.mimeTypes == "string"
          ? this.mimeTypes.split(",")
          : this.mimeTypes,
      lastRefresh: this.lastRefreshId
    };
  },
  computed: {
    _fs_class: function () {
      return {
        "cat-document-upload-simple": true,
        dropzone: true,
        invalid:
          this.errs &&
          this.errs.length > 0 &&
          this.errs.some(function (r) {
            return r.level <= 1;
          }),
        warning:
          this.errs &&
          this.errs.length > 0 &&
          this.errs.some(function (r) {
            return r.level === 2;
          }),
        infomessage:
          this.errs &&
          this.errs.length > 0 &&
          this.errs.some(function (r) {
            return r.level === 3;
          }),
        debugmessage:
          this.errs &&
          this.errs.length > 0 &&
          this.errs.some(function (r) {
            return r.level === 4;
          }),
        required: this.mandatory,
        optional: !this.mandatory
      };
    }
  },
  watch: {
    errors: function (newErrors) {
      this.errs = newErrors ? newErrors : [];
    },
    lastRefreshId: function (newId) {
      if (this.lastRefresh !== newId) {
        this.lastRefresh = newId;
        // Remove all local errors (errors from the file upload)
        this.errs = this.errs.filter(function (err) {
          return !err.local;
        });
      }
    },
    rejectedUploads: function (newCount, oldCount) {
      if (newCount > oldCount) {
        this.errs.push({
          msg: this.resolveTxt("document.upload.transfer.failed"),
          level: 1,
          local: true
        });
        this.emitFileUploadError(this.errs);
      }
    }
  },
  methods: {
    clickUpload: function (e) {
      e.stopPropagation();
      this.$refs.fileUpload.click();
    },
    isDragAndDropCapable: function () {
      /*
          Create a test element to see if certain events
          are present that let us do drag and drop.
        */
      var div = document.createElement("div");

      /*
          Check to see if the `draggable` event is in the element
          or the `ondragstart` and `ondrop` events are in the element. If
          they are, then we have what we need for dragging and dropping files.

          We also check to see if the window has `FormData` and `FileReader` objects
          present so we can do our AJAX uploading
        */
      return (
        ("draggable" in div || ("ondragstart" in div && "ondrop" in div)) &&
        "FormData" in window &&
        "FileReader" in window
      );
    },
    _onChange: function (event) {
      event.preventDefault();
      event.stopPropagation();

      let files = event.target.files || event.dataTransfer.files;
      if (!files.length) {
        // This block is for IE only
        // IE triggers change event when the file upload value is reset
        return false;
      } else {
        // Remove any errors added by the component
        // Reset the errors only if a valid change event
        this.errs = this.errs.filter((error) => !error.local);
      }

      // Check we don't have too many files
      if (files.length > this.maxFilesAllowed) {
        this.errs.push({
          msg: this.tooManyFilesError
            ? this.tooManyFilesError
            : this.resolveTxt("skin.documents.tooMany", "Too many files"),
          level: 2,
          local: true
        });
        this.emitFileUploadError(this.errs);
        return;
      }

      let formData = new FormData();

      // Check all of the files and add them to the form data
      for (let f = 0; f < files.length; f++) {
        const file = files[f];

        // File extension
        if (this.extension && this.extension.length > 0) {
          let extensionValid = false;
          for (let i in this.extension) {
            if (
              new RegExp(".*." + this.extension[i] + "$").test(
                file.name.toLowerCase()
              )
            ) {
              extensionValid = true;
              break;
            }
          }
          if (!extensionValid) {
            this.errs.push({
              msg: this.fileTypeError
                ? this.resolveTxt(this.fileTypeError, this.fileTypeError)
                : this.resolveTxt(
                    "skin.document.fileType.notAllowed",
                    "The selected file format is not supported for upload. Please choose a file in one of the following formats: "
                  ) + this.extension.join(", ").toUpperCase(),
              level: 1,
              local: true
            });
            this.emitFileUploadError(this.errs);
            return;
          }
        }

        // File doc type
        if (this.documentTypes && this.documentTypes.length > 0) {
          // file.type will be populated by the browser only for common file types. Uncommon file extensions will return an empty string e.g. wpd (Word Perfect).
          let typeValid = !file.type;
          for (let i in this.documentTypes) {
            if (file.type === this.documentTypes[i]) {
              typeValid = true;
              break;
            }
          }
          if (!typeValid) {
            this.errs.push({
              msg: this.fileTypeError
                ? this.resolveTxt(this.fileTypeError, this.fileTypeError)
                : this.resolveTxt(
                    "skin.document.docType.notAllowed",
                    "The selected file format is not supported for upload. Please choose a file in one of the following formats: "
                  ) + this.extension.join(", ").toUpperCase(),
              level: 1,
              local: true
            });
            this.emitFileUploadError(this.errs);
            return;
          }
          if (file.name.indexOf("/") != -1) {
            file.name = file.name.substr(file.name.lastIndexOf("/") + 1);
          }
          if (file.name.indexOf("\\") != -1) {
            file.name = file.name.substr(file.name.lastIndexOf("\\") + 1);
          }
        }

        // File max size
        if (this.maxSize) {
          if (parseFloat(this.maxSize) < parseFloat(file.size)) {
            this.errs.push({
              msg: this.fileSizeError
                ? this.resolveTxt(this.fileSizeError, this.fileSizeError)
                : this.resolveTxt(
                    "skin.document.fileSize.tooLarge",
                    "The selected file is too large. Please select a file less than the maximum allowed file size of "
                  ) + this.formatBytes(this.maxSize),
              level: 1,
              local: true
            });

            this.emitFileUploadError(this.errs);

            return;
          }
        }

        // File empty
        if (file.size === 0) {
          this.errs.push({
            msg: this.fileSizeZeroError
              ? this.resolveTxt(this.fileSizeZeroError, this.fileSizeZeroError)
              : this.resolveTxt(
                  "skin.document.fileSize.empty",
                  "File must be not be empty"
                ),
            level: 2,
            local: true
          });
          this.emitFileUploadError(this.errs);
          return;
        }

        formData.append(this.alias, file);
      }

      // Temporary until the command is parsed
      formData.append("nodeId", this.id);

      // Fire an event and let the glue handle it
      this.$emit("ui-file-upload", {
        event: event,
        nodeId: this.id,
        formData: formData
      });

      // Reset so onchange triggered by the browser after file selection can be fired again with the same file, including after being removed.
      // The fileUpload input is hidden, so is safe to do this here.
      if (this.$refs.fileUpload) {
        this.$refs.fileUpload.value = "";
      }

      return false;
    },
    emitFileUploadError: function (errs, reset) {
      this.$emit("ui-file-upload-error", {
        errors: errs
      });
    },
    formatBytes: function (bytes, decimals = 2) {
      if (!bytes) return "0Bytes";

      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))}${sizes[i]}`;
    }
  }
});
</script>
